/**
 * Step 05 — two colours, picked as a PAIR (spec §2.2).
 *
 * A first-time user is not being asked to art-direct four roles; they are
 * being asked which of eight combinations looks like them. Text colour carries
 * the words, the highlight carries the one word that matters and the speaker's
 * name. Paper and Muted have no render effect and are not asked (§10.2).
 *
 * "Custom" opens two tolerant fields (`#abc`, `abc123`, `#AABBCC`), and an
 * uploaded logo offers its own colours — the brand owner usually wants exactly
 * those.
 */
import { useEffect, useState, type Dispatch } from "react";
import { Pipette } from "lucide-react";
import { normalizeHex } from "../../../components/StudioInspector";
import { swatchesFromImage } from "../../../lib/brandSetup/logoColors";
import { chipScope, type BrandSetupAction, type BrandSetupState } from "../../../lib/brandSetup/state";
import { MONO_LABEL, REASON } from "../ui";

const HEX = {
  white: "#F8F8F8",
  cream: "#F2EBD8",
  yellow: "#FFD500",
  gold: "#FFBF17",
  cyan: "#5EEAD4",
  coral: "#FF7A59",
  mint: "#86EFAC",
  pink: "#F9A8D4",
  orange: "#FB923C",
  lavender: "#C4B5FD",
} as const;

/** The eight curated pairs, spec §2.2 — text colour first, highlight second. */
export const COLOR_PAIRS: ReadonlyArray<{ id: string; label: string; ink: string; accent: string }> = [
  { id: "white-yellow", label: "White · Yellow", ink: HEX.white, accent: HEX.yellow },
  { id: "cream-gold", label: "Cream · Gold", ink: HEX.cream, accent: HEX.gold },
  { id: "white-cyan", label: "White · Cyan", ink: HEX.white, accent: HEX.cyan },
  { id: "white-coral", label: "White · Coral", ink: HEX.white, accent: HEX.coral },
  { id: "white-mint", label: "White · Mint", ink: HEX.white, accent: HEX.mint },
  { id: "white-pink", label: "White · Pink", ink: HEX.white, accent: HEX.pink },
  { id: "cream-orange", label: "Cream · Orange", ink: HEX.cream, accent: HEX.orange },
  { id: "white-lavender", label: "White · Lavender", ink: HEX.white, accent: HEX.lavender },
];

const BAD_HEX = "Enter a color like #FFD500";

export function ColorsStep(props: {
  state: BrandSetupState;
  dispatch: Dispatch<BrandSetupAction>;
}): React.JSX.Element {
  const { state, dispatch } = props;
  const [draft, setDraft] = useState<{ ink?: string; accent?: string }>({});
  const [swatches, setSwatches] = useState<string[]>([]);

  const logoUploaded = state.logoKind === "upload" && !!state.logoDataUrl;
  useEffect(() => {
    if (!logoUploaded || !state.logoDataUrl) {
      setSwatches([]);
      return;
    }
    let live = true;
    swatchesFromImage(state.logoDataUrl)
      .then((s) => { if (live) setSwatches(s); })
      // A logo we can't decode simply has no colours to offer — no row, no
      // error: the eight pairs are still right there.
      .catch(() => { if (live) setSwatches([]); });
    return () => { live = false; };
  }, [logoUploaded, state.logoDataUrl]);

  const setPair = (pair: { id: string; ink: string; accent: string }): void => {
    setDraft({});
    dispatch({ type: "colors", colors: { ink: pair.ink, accent: pair.accent } });
    dispatch({ type: "patch", patch: { colorPair: pair.id } });
  };

  const setRole = (role: "ink" | "accent", raw: string): void => {
    setDraft((d) => ({ ...d, [role]: raw }));
    const hex = normalizeHex(raw);
    // An invalid entry keeps the LAST VALID value on screen and says so —
    // never a half-typed hex painted onto the preview.
    if (!hex) return;
    dispatch({ type: "colors", colors: { [role]: hex.toUpperCase() } });
    dispatch({ type: "patch", patch: { colorPair: "custom" } });
  };

  const colorChips = state.chips.filter((c) => chipScope(c.field) === "colors");
  const fromReel = colorChips.filter((c) => state.accepted[c.field] !== false);
  const matchesCurated = COLOR_PAIRS.some((p) => samePair(p, state.colors.ink, state.colors.accent));
  // Custom opens when it was chosen, when the study prefilled a pair of its
  // own, or when the colours on screen are nobody's curated pair.
  const custom =
    state.colorPair === "custom" ||
    (state.colorPair === null && (fromReel.length > 0 || !matchesCurated));

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {fromReel.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span
            style={{
              ...MONO_LABEL,
              color: "var(--ql-yellow)",
              padding: "4px 9px",
              borderRadius: 999,
              border: "1px solid rgba(255,213,0,.35)",
              background: "rgba(255,213,0,.06)",
            }}
          >
            From your reel
          </span>
          <button
            type="button"
            onClick={() => fromReel.forEach((c) => dispatch({ type: "resetChip", field: c.field }))}
            style={{
              border: "none",
              background: "transparent",
              color: "var(--ql-fg-muted)",
              fontSize: 13,
              fontFamily: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0,
            }}
          >
            Use ours instead
          </button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))", gap: 10 }}>
        {COLOR_PAIRS.map((pair, i) => {
          // DISPLAY only — still no dispatch on mount (an untouched walk must
          // write no colour keys). The wizard opens on cream/gold, so with
          // nothing lit the operator saw eight pairs and no answer to "which
          // one am I on?" while the phone was plainly showing one of them.
          const on =
            state.colorPair === pair.id ||
            (state.colorPair === null &&
              fromReel.length === 0 &&
              samePair(pair, state.colors.ink, state.colors.accent));
          return (
            <button
              key={pair.id}
              type="button"
              {...(i === 0 ? { "data-autofocus": true } : {})}
              onClick={() => setPair(pair)}
              aria-pressed={on}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 13px",
                borderRadius: 14,
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "left",
                border: on ? "1.5px solid var(--ql-yellow)" : "1px solid rgba(255,255,255,.10)",
                background: on ? "rgba(255,213,0,.07)" : "rgba(255,255,255,.02)",
                transition: "all .15s var(--ql-ease)",
              }}
            >
              <span style={{ display: "inline-flex", flexShrink: 0 }}>
                <Circle color={pair.ink} />
                <Circle color={pair.accent} overlap />
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ql-fg)", minWidth: 0 }}>{pair.label}</span>
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => dispatch({ type: "patch", patch: { colorPair: "custom" } })}
          aria-pressed={custom}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 13px",
            borderRadius: 14,
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "left",
            border: custom ? "1.5px solid var(--ql-yellow)" : "1px dashed rgba(255,255,255,.16)",
            background: custom ? "rgba(255,213,0,.07)" : "rgba(255,255,255,.02)",
          }}
        >
          <span style={{ display: "inline-flex", flexShrink: 0 }}>
            <Circle color={state.colors.ink} />
            <Circle color={state.colors.accent} overlap />
          </span>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ql-fg)" }}>Custom</span>
        </button>
      </div>

      {custom && (
        <div
          style={{
            display: "grid",
            gap: 14,
            padding: 16,
            borderRadius: 16,
            border: "1px solid rgba(255,255,255,.08)",
            background: "rgba(255,255,255,.02)",
          }}
        >
          <ColorRow
            label="Text color"
            value={state.colors.ink}
            draft={draft.ink}
            onChange={(v) => setRole("ink", v)}
          />
          <ColorRow
            label="Highlight color"
            value={state.colors.accent}
            draft={draft.accent}
            onChange={(v) => setRole("accent", v)}
          />
        </div>
      )}

      {swatches.length > 0 && (
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ ...MONO_LABEL, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Pipette size={12} />
            From your logo
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {swatches.map((hex) => (
              <button
                key={hex}
                type="button"
                title={`Use ${hex} as the highlight`}
                onClick={() => setRole("accent", hex)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 12px 7px 8px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,.10)",
                  background: "rgba(255,255,255,.02)",
                  color: "var(--ql-fg-muted)",
                  fontFamily: "var(--ql-font-mono)",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                <span
                  aria-hidden
                  style={{ width: 18, height: 18, borderRadius: 999, background: hex, border: "1px solid rgba(255,255,255,.2)" }}
                />
                {hex}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Case-insensitive, because a hex typed by hand and a hex from a preset are
 *  the same colour whatever the shift key was doing. */
function samePair(pair: { ink: string; accent: string }, ink: string, accent: string): boolean {
  return pair.ink.toLowerCase() === ink.toLowerCase() && pair.accent.toLowerCase() === accent.toLowerCase();
}

function Circle({ color, overlap }: { color: string; overlap?: boolean }): React.JSX.Element {
  return (
    <span
      aria-hidden
      style={{
        width: 22,
        height: 22,
        borderRadius: 999,
        background: color,
        border: "1px solid rgba(255,255,255,.22)",
        marginLeft: overlap ? -8 : 0,
        boxShadow: overlap ? "-2px 0 0 rgba(0,0,0,.35)" : "none",
      }}
    />
  );
}

function ColorRow(props: {
  label: string;
  value: string;
  draft: string | undefined;
  onChange: (raw: string) => void;
}): React.JSX.Element {
  const { label, value, draft } = props;
  const shown = draft ?? value;
  const bad = draft !== undefined && normalizeHex(draft) === null;
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <label
          style={{
            position: "relative",
            width: 40,
            height: 40,
            borderRadius: 12,
            flexShrink: 0,
            cursor: "pointer",
            background: value,
            border: "1px solid rgba(255,255,255,.18)",
          }}
        >
          <input
            type="color"
            value={value}
            onChange={(e) => props.onChange(e.target.value)}
            aria-label={`${label} picker`}
            style={{ position: "absolute", inset: 0, opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
          />
        </label>
        <div style={{ display: "grid", gap: 4, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ql-fg)" }}>{label}</span>
          <input
            value={shown}
            spellCheck={false}
            onChange={(e) => props.onChange(e.target.value)}
            aria-label={`${label} hex code`}
            style={{
              width: "100%",
              maxWidth: 160,
              padding: "8px 11px",
              borderRadius: 10,
              border: `1px solid ${bad ? "var(--ql-danger)" : "rgba(255,255,255,.10)"}`,
              background: "rgba(17,24,39,.5)",
              color: "var(--ql-fg)",
              fontFamily: "var(--ql-font-mono)",
              fontSize: 13,
              letterSpacing: ".04em",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>
      {bad && <div style={{ ...REASON, fontSize: 12.5 }}>{BAD_HEX}</div>}
    </div>
  );
}
